import { useState } from 'react';
import { useResource } from '../lib/useResource.js';
import { post, patch, get } from '../lib/api.js';
import { formatBRL, parseCentsInput } from '../lib/format.js';
import {
  Badge,
  Card,
  ErrorBox,
  Field,
  Modal,
  PageHeader,
  useSubmit,
  useToast,
} from '../components/ui.js';
import { Tabela } from '../components/Tabela.js';
import { useSession } from '../lib/session.js';

interface Candidate {
  id: string;
  sku: string;
  name: string;
  type: 'product' | 'service' | 'package';
  brand: string | null;
  category: string | null;
  unit: string;
  packageSize: string | null;
  status: 'active' | 'inactive' | 'on_request';
  availability: 'available' | 'unavailable' | 'check';
}

interface ItemDetail extends Candidate {
  description: string | null;
  synonyms: string[];
  durationMinutes: number | null;
  requiresResource: boolean;
  costCents?: number | null;
}

const STATUS_LABEL: Record<Candidate['status'], string> = {
  active: 'ativo',
  inactive: 'inativo',
  on_request: 'sob consulta',
};
const AVAILABILITY_LABEL: Record<Candidate['availability'], string> = {
  available: 'disponível',
  unavailable: 'indisponível',
  check: 'consultar',
};

export function Catalog() {
  const { can } = useSession();
  const toast = useToast();
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [novo, setNovo] = useState(false);
  const [precoDe, setPrecoDe] = useState<Candidate | null>(null);

  const lista = useResource<{ items: Candidate[] }>(
    `/api/catalog/search?term=${encodeURIComponent(query)}&limit=100`,
  );

  return (
    <>
      <PageHeader
        title="Produtos e serviços"
        description="Preço ausente é sob consulta, nunca zero. Item inativo não é oferecido nem entra em orçamento."
        actions={
          can('catalog.write') ? (
            <button type="button" className="primary" onClick={() => setNovo(true)}>
              Novo item
            </button>
          ) : null
        }
      />

      <form
        className="toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(term);
        }}
      >
        <div className="grow">
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Nome, SKU, marca ou sinônimo"
            aria-label="Buscar no catálogo"
          />
        </div>
        <button type="submit">Buscar</button>
      </form>

      <ErrorBox error={lista.error} />

      <Card tight>
        <Tabela
          linhas={lista.data?.items ?? []}
          chave={(i) => i.id}
          vazio={query ? 'Nada encontrado com esse termo.' : 'Catálogo vazio.'}
          vazioDetalhe={
            query
              ? 'Produto parecido não é substituto automático: cadastre o item certo.'
              : 'Cadastre os itens reais da loja antes de operar.'
          }
          colunas={[
            {
              chave: 'item',
              rotulo: 'Item',
              tipo: 'principal',
              render: (item) => (
                <>
                  {item.name}
                  {item.brand ? <div className="small muted">{item.brand}</div> : null}
                </>
              ),
            },
            { chave: 'sku', rotulo: 'SKU', render: (item) => <span className="mono small">{item.sku}</span> },
            {
              chave: 'tipo',
              rotulo: 'Tipo',
              render: (item) =>
                item.type === 'product' ? 'produto' : item.type === 'service' ? 'serviço' : 'pacote',
            },
            {
              chave: 'categoria',
              rotulo: 'Categoria',
              ocultarNoCelular: true,
              render: (item) => item.category ?? '—',
            },
            {
              chave: 'situacao',
              rotulo: 'Situação',
              render: (item) => (
                <Badge
                  tone={item.status === 'active' ? 'ok' : item.status === 'inactive' ? 'critical' : 'attention'}
                >
                  {STATUS_LABEL[item.status]}
                </Badge>
              ),
            },
            {
              chave: 'disponibilidade',
              rotulo: 'Disponibilidade',
              render: (item) => AVAILABILITY_LABEL[item.availability],
            },
            {
              chave: 'acoes',
              rotulo: '',
              tipo: 'actions',
              render: (item) =>
                can('catalog.write') ? (
                  <button type="button" className="small" onClick={() => setPrecoDe(item)}>
                    Preço e edição
                  </button>
                ) : null,
            },
          ]}
        />
      </Card>

      {novo ? (
        <NovoItem
          onClose={() => setNovo(false)}
          onCreated={() => {
            setNovo(false);
            lista.reload();
            toast('Item cadastrado.');
          }}
        />
      ) : null}

      {precoDe ? (
        <EditarItem
          item={precoDe}
          onClose={() => setPrecoDe(null)}
          onSaved={() => {
            setPrecoDe(null);
            lista.reload();
            toast('Item atualizado.');
          }}
        />
      ) : null}
    </>
  );
}

function NovoItem({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { can } = useSession();
  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<'product' | 'service' | 'package'>('product');
  const [category, setCategory] = useState('');
  const [unit, setUnit] = useState('un');
  const [synonyms, setSynonyms] = useState('');
  const [price, setPrice] = useState('');
  const [cost, setCost] = useState('');
  const [duration, setDuration] = useState('');
  const [availability, setAvailability] = useState<'available' | 'unavailable' | 'check'>('check');

  const { submit, saving, error } = useSubmit(async () => {
    const costCents = cost ? parseCentsInput(cost) : null;
    const created = await post<{ item: { id: string } }>('/api/catalog/items', {
      sku,
      name,
      type,
      category: category || null,
      unit,
      availability,
      synonyms: synonyms
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      durationMinutes: duration ? Number(duration) : null,
      requiresResource: type === 'service' && Boolean(duration),
      costCents,
    });
    if (price) {
      const cents = parseCentsInput(price);
      if (cents === null) throw new Error('Preço inválido. Use o formato 199,90.');
      await post(`/api/catalog/items/${created.item.id}/price`, { price });
    }
    onCreated();
  });

  return (
    <Modal
      title="Novo item"
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="form-item" className="primary" disabled={saving}>
            {saving ? 'Salvando…' : 'Cadastrar'}
          </button>
        </>
      }
    >
      <form id="form-item" onSubmit={submit}>
        <ErrorBox error={error} />
        <div className="field-row">
          <Field label="Nome comercial" required>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </Field>
          <Field label="SKU" required hint="Identificador estável do item.">
            <input type="text" value={sku} onChange={(e) => setSku(e.target.value)} required />
          </Field>
        </div>
        <div className="field-row">
          <Field label="Tipo">
            <select value={type} onChange={(e) => setType(e.target.value as typeof type)}>
              <option value="product">Produto</option>
              <option value="service">Serviço</option>
              <option value="package">Pacote</option>
            </select>
          </Field>
          <Field label="Categoria">
            <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} />
          </Field>
          <Field label="Unidade">
            <input type="text" value={unit} onChange={(e) => setUnit(e.target.value)} />
          </Field>
        </div>
        <div className="field-row">
          <Field label="Preço de venda" hint={'Deixe em branco para "sob consulta".'}>
            <input type="text" value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" placeholder="199,90" />
          </Field>
          <Field label="Disponibilidade">
            <select value={availability} onChange={(e) => setAvailability(e.target.value as typeof availability)}>
              <option value="check">Consultar</option>
              <option value="available">Disponível</option>
              <option value="unavailable">Indisponível</option>
            </select>
          </Field>
          {type === 'service' ? (
            <Field label="Duração (minutos)" hint="Necessária para oferecer horários na agenda.">
              <input type="number" min={1} value={duration} onChange={(e) => setDuration(e.target.value)} />
            </Field>
          ) : null}
        </div>
        <Field label="Sinônimos e nomes populares" hint="Separados por vírgula. Ajudam a busca, não decidem variante.">
          <input type="text" value={synonyms} onChange={(e) => setSynonyms(e.target.value)} />
        </Field>
        {can('catalog.cost.read') ? (
          <Field
            label="Custo de compra"
            hint="Privado. Nunca sai para a IA nem para o atendente, e não aparece em orçamento."
          >
            <input type="text" value={cost} onChange={(e) => setCost(e.target.value)} inputMode="decimal" />
          </Field>
        ) : null}
      </form>
    </Modal>
  );
}

function EditarItem({
  item,
  onClose,
  onSaved,
}: {
  item: Candidate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { can } = useSession();
  const detalhe = useResource<{ item: ItemDetail | null }>(`/api/catalog/items/${item.id}`);
  const [price, setPrice] = useState('');
  const [status, setStatus] = useState(item.status);
  const [availability, setAvailability] = useState(item.availability);

  const { submit, saving, error } = useSubmit(async () => {
    if (status !== item.status || availability !== item.availability) {
      await patch(`/api/catalog/items/${item.id}`, { status, availability });
    }
    if (price.trim()) {
      const cents = parseCentsInput(price);
      if (cents === null) throw new Error('Preço inválido. Use o formato 199,90.');
      await post(`/api/catalog/items/${item.id}/price`, { price });
    }
    onSaved();
  });

  return (
    <Modal
      title={item.name}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="form-editar" className="primary" disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </>
      }
    >
      <form id="form-editar" onSubmit={submit}>
        <ErrorBox error={error} />
        <p className="small muted">
          Alterar o preço cria uma nova versão e marca como obsoleta toda proposta pendente daquele
          item. A proposta já aceita não muda.
        </p>
        <Field label="Novo preço de venda" hint="Deixe em branco para manter o preço atual.">
          <input type="text" value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" placeholder="199,90" />
        </Field>
        <div className="field-row">
          <Field label="Situação">
            <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              <option value="active">Ativo</option>
              <option value="on_request">Sob consulta</option>
              <option value="inactive">Inativo</option>
            </select>
          </Field>
          <Field label="Disponibilidade">
            <select value={availability} onChange={(e) => setAvailability(e.target.value as typeof availability)}>
              <option value="check">Consultar</option>
              <option value="available">Disponível</option>
              <option value="unavailable">Indisponível</option>
            </select>
          </Field>
        </div>
        {can('catalog.cost.read') && detalhe.data?.item?.costCents != null ? (
          <p className="small muted">
            Custo de compra registrado: {formatBRL(detalhe.data.item.costCents)} — visível apenas para
            quem tem permissão.
          </p>
        ) : null}
      </form>
    </Modal>
  );
}

export { get };
